import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { checkMultiplicity } from 'nmr-processing';
import { useMemo } from 'react';
import { useAssignment, } from '../../assignment/AssignmentsContext';
import { useChartData } from '../../context/ChartContext';
import { useScaleChecked } from '../../context/ScaleContext';
import { HighlightEventSource, useHighlight } from '../../highlight';
import useSpectrum from '../../hooks/useSpectrum';
import { hasCouplingConstant } from '../../panels/extra/utilities/MultiplicityUtilities';
import { AssignmentActionsButtons } from '../ranges/AssignmentActionsButtons';
import LevelNode from './LevelNode';
import StringNode from './StringNode';
import TreeNodes from './TreeNodes';
import createTreeNodes from './buildTreeNode';
const styles = {
    cursor: 'default',
    opacity: 0.6,
    strokeWidth: 1,
};
const cssStyle = css `
  .signal-target {
    visibility: hidden;
  }

  &:hover {
    .signal-target {
      visibility: visible;
    }
  }
`;
const BOX_PADDING = 5;
function extractID(assignment) {
    return [assignment.id].concat(assignment.assigned?.x || []);
}
function InnerMultiplicityTree({ labelOptions, onUnlink, data, width: widthProp, index, }) {
    const { startX, signal, width, height, startY } = data;
    const { scaleX } = useScaleChecked();
    const showLabels = width / widthProp >= 0.1;
    labelOptions = labelOptions || {
        distance: 10,
        fontSize: 11,
    };
    const assignment = useAssignment(signal.id);
    const highlight = useHighlight(extractID(assignment), {
        type: HighlightEventSource.SIGNAL,
    });
    function assignHandler() {
        assignment.setActive('x');
    }
    function unAssignHandler() {
        onUnlink(index);
    }
    const isHighlighted = highlight.isActive || assignment.isActive;
    return (_jsxs("g", { style: isHighlighted ? { ...styles, opacity: 1, strokeWidth: 1.5 } : styles, onMouseEnter: () => {
            assignment.show('x');
            highlight.show();
        },
        onMouseLeave: () => {
            assignment.hide();
            highlight.hide();
        }, pointerEvents: "bounding-box", ...(!assignment.isActive && { css: cssStyle }), children: [drawTree(data, { scaleX, labelOptions, showLabels }), _jsx("rect", { x: startX - BOX_PADDING, y: startY - BOX_PADDING, width: width + BOX_PADDING * 2, height: height + BOX_PADDING * 2, fill: isHighlighted ? '#ff6f0057' : 'transparent', "data-no-export": "true" }), _jsx(AssignmentActionsButtons, { className: "signal-target", isActive: !!(assignment.isActive || signal?.diaIDs), y: startY, x: scaleX()(signal.delta) - 30, onAssign: assignHandler, onUnAssign: unAssignHandler, borderRadius: 16 })] }));
}
export function MultiplicityTree(props) {
    const { width } = useChartData();
    const spectrum = useSpectrum(null);
    const { scaleX, scaleY } = useScaleChecked();
    const treeData = useMemo(() => spectrum
        ? getTree(props.range, spectrum, { scaleX, scaleY })
        : [], [props.range, scaleX, scaleY, spectrum]);
    if (!spectrum)
        return null;
    return (_jsx("g", { children: treeData?.map((data, index) => (_jsx(InnerMultiplicityTree, { index: index, data: data, width: width, ...props }, data.signal.id))) }));
}
function getTree(range, spectrum, scale) {
    const SHIFT_X = 30;
    const treeResult = [];
    const { signals = [], ...otherRangeProps } = range;
    for (const signal of signals) {
        const isMassive = !checkMultiplicity(signal.multiplicity, ['m']);
        const length = signal?.multiplicity?.length || 0;
        let width = 0;
        let height = 0;
        let levelHeight = 0;
        let nodes = [];
        let startX = 0;
        const { from, to } = range;
        if (signal.multiplicity) {
            const buildTreeNodesData = createTreeNodes(signal, spectrum);
            const jIndices = signal.multiplicity
                .split('')
                .map((_mult, i) => (hasCouplingConstant(_mult) ? i : undefined))
                .filter((_i) => _i !== undefined);
            nodes = buildTreeNodesData(0, jIndices, [], signal.delta);
        }
        // +2 because of multiplicity text and start level node before the actual tree starts
        // 2* for levels between nodes (edges)
        if (isMassive) {
            startX = scale.scaleX()(to);
            width = Math.abs(scale.scaleX()(from)) - Math.abs(startX);
            height = width / 3;
            levelHeight = height / (length + 2);
        }
        else {
            const treeRange = getTreeRange(nodes, signal.delta);
            startX = scale.scaleX()(treeRange.to);
            width = Math.abs(scale.scaleX()(treeRange.from)) - Math.abs(startX);
            height = width / 2;
            if (signal.multiplicity !== 's') {
                levelHeight = height / (2 * length + 2);
                height = width / 2;
            }
            else {
                levelHeight = 3;
                height = levelHeight * 4;
            }
        }
        const startY = scale.scaleY(spectrum.id)(getStartY(spectrum, { from, to })) -
            height -
            SHIFT_X;
        treeResult.push({
            isMassive,
            startX,
            startY,
            width,
            height,
            levelHeight,
            nodes,
            signal,
            range: otherRangeProps,
        });
    }
    return treeResult;
}
function getStartY(spectrum, options) {
    const { data: { x, re }, } = spectrum;
    const { from, to } = options;
    let max = Number.NEGATIVE_INFINITY;
    for (const i in x) {
        if (x[i] >= from && x[i] <= to && re[i] > max) {
            max = re[i];
        }
    }
    return max;
}
function getTreeRange(nodes, delta) {
    const range = { from: delta, to: delta };
    for (const node of nodes) {
        if (node._startX < range.from) {
            range.from = node._startX;
        }
        if (node._startX > range.to) {
            range.to = node._startX;
        }
    }
    return range;
}
function drawTree(treeData, { scaleX, labelOptions, showLabels }) {
    const { isMassive, signal, levelHeight, range, nodes, startY } = treeData;
    // const { levelHeight } = treeProps;
    // first tree level
    const firstLevelStartY = startY;
    let _startY = firstLevelStartY;
    // second tree level
    const secondLevelStartY = startY + levelHeight;
    _startY = secondLevelStartY;
    // third tree level
    _startY += levelHeight;
    if (isMassive) {
        const _rangeFrom = scaleX()(range.from);
        const _rangeTo = scaleX()(range.to);
        const pathData = `M ${_rangeFrom} ${_startY + levelHeight} ${_rangeFrom} ${_startY} ${scaleX()(signal.delta)} ${_startY} ${_rangeTo} ${_startY} ${_rangeTo} ${_startY + levelHeight}`;
        return (_jsxs("g", { children: [_jsx(StringNode, { signal: signal, startY: firstLevelStartY, levelHeight: levelHeight, fontSize: labelOptions.fontSize, showLabels: showLabels }), _jsx(LevelNode, { signal: signal, startY: secondLevelStartY, levelHeight: levelHeight }), _jsx("path", { d: pathData, stroke: "blue", fill: "none" })] }));
    }
    return (_jsxs("g", { children: [_jsx(StringNode, { signal: signal, startY: firstLevelStartY, levelHeight: levelHeight, fontSize: labelOptions.fontSize, showLabels: showLabels }), _jsx(LevelNode, { signal: signal, startY: secondLevelStartY, levelHeight: levelHeight }), _jsx(TreeNodes, { nodesData: nodes, signalID: nodes, showLabels: showLabels, startY: startY, labelOptions: labelOptions, levelHeight: levelHeight })] }));
}
